import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Pricing } from "@/components/Pricing";
import { PricingFaq } from "@/components/PricingFaq";
import { SampleComparison } from "@/components/SampleComparison";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PromptBox } from "@/components/PromptBox";
import { TEMPLATES } from "@/components/templates";
import { TestimonialWall } from "@/components/TestimonialWall";
import { PublicFooter } from "@/components/PublicFooter";
import content from "@/seo/content.json";

const FEATURES = [
  ["Site analysis", "For address-based studies we review the property itself: access, visibility, parking, nearby competitors, likely zoning and the surrounding household base."],
  ["Market demand", "Market size (TAM, SAM and SOM), the competitive set and growth trends, with the assumptions behind each figure stated plainly."],
  ["Financial feasibility", "Startup costs, five-year projections, break-even timing and return on investment, laid out so a credit officer can follow the math."],
  ["Technical requirements", "Equipment, infrastructure and build-out needs, along with the practical hurdles that tend to delay an opening."],
  ["Operations", "Staffing plan, launch milestones and the supply relationships the business will depend on."],
  ["Legal and regulatory", "Permits, licenses and a compliance checklist to confirm with the city, county and state."],
  ["Risk and SWOT", "A risk matrix rating likelihood and impact, with a specific mitigation for each item."],
  ["Revisions", "If your lender or investor asks for changes, we revise the affected sections and reissue the study."],
];

const STEPS = [
  ["Describe the project", "Tell us about the business, or give us the property address and what you intend to build or operate there."],
  ["We prepare the study", "An analyst assembles market, site and financial research and reviews every section before it reaches you."],
  ["Deliver it to your lender", "Read the study in your private client portal, then download it as PDF or Word for your loan package."],
];

const SAMPLE = [["Project", "Express car wash, Plano, TX"], ["Feasibility score", "78 / 100"], ["Verdict", "Conditional Go"], ["Break-even", "26 months"], ["Debt service coverage", "1.42x (year 2)"]];

const SampleSheet = () => (
  <div className="paper-sheet rounded-sm p-7 sm:p-8 fade-up" style={{ animationDelay: "120ms" }} data-testid="hero-sample-sheet">
    <div className="flex items-baseline justify-between border-b border-slate-400/60 pb-3 mb-5">
      <span className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Feasibility study</span>
      <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Illustrative</span>
    </div>
    <p className="font-serif-display text-3xl leading-tight text-slate-900 mb-1">Executive summary</p>
    <p className="text-sm text-slate-600 mb-6">Prepared for the borrower and First Community Bank</p>
    <dl className="text-sm">
      {SAMPLE.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-4 py-2 border-b border-slate-300/80 last:border-0">
          <dt className="text-slate-600">{k}</dt><dd className="num font-medium text-slate-900 text-right">{v}</dd>
        </div>
      ))}
    </dl>
    <p className="text-xs text-slate-500 mt-6 italic">Figures shown are an example only and do not describe a real client.</p>
  </div>
);

const VisitorHero = () => (
  <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
    <div className="lg:col-span-7 fade-up">
      <p className="text-xs uppercase tracking-[0.25em] text-slate-400 mb-6">Independent feasibility studies</p>
      <h1 data-testid="visitor-hero-title" className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mb-7 text-slate-50">Know whether the project works before you commit the capital.</h1>
      <p className="text-slate-300 text-base md:text-lg max-w-xl mb-10 leading-relaxed">We prepare feasibility studies for new businesses, specific properties and franchise purchases. Each one covers market, site, financial, legal and risk analysis and closes with a clear recommendation your lender can rely on.</p>
      <div className="flex flex-wrap items-center gap-3" data-testid="visitor-hero-ctas">
        <Button asChild data-testid="hero-see-pricing" className="h-12 px-7 rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400 font-semibold"><Link to="/#pricing">View study types and fees<ArrowRight className="h-4 w-4 ml-2" /></Link></Button>
        <Button asChild variant="outline" data-testid="hero-borrowers" className="h-12 px-7 rounded-full border-slate-600 bg-transparent text-slate-100 hover:bg-slate-800"><Link to="/feasibility-studies">Studies for SBA and bank loans</Link></Button>
        <Link to="/login" data-testid="hero-staff-login" className="text-sm text-slate-500 hover:text-slate-300 underline-offset-4 hover:underline ml-2">Staff sign in</Link>
      </div>
      <p className="mt-12 pt-6 border-t border-slate-800 text-sm text-slate-400 max-w-xl">Commonly requested for SBA 7(a) and 504 loans, conventional bank financing, construction lending and franchise purchases.</p>
    </div>
    <div className="lg:col-span-5"><SampleSheet /></div>
  </div>
);

const StaffHero = () => (
  <div className="max-w-4xl mx-auto text-center fade-up">
    <p className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-5">AI feasibility study generator</p>
    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] mb-5">What feasibility study do you want to <span className="text-emerald-400">generate</span> today?</h1>
    <p className="text-slate-400 text-base md:text-lg max-w-2xl mx-auto mb-10">Describe a business idea or enter a property address. Get a complete, editable study covering market, site, financial, legal, operations, risks and a Go / No-Go verdict.</p>
    <PromptBox />
  </div>
);

const Templates = ({ user }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
    <div className="grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-4 lg:sticky lg:top-24 self-start">
        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-4">Types of study we prepare</h2>
        <p className="text-slate-400 mb-8 leading-relaxed">Most engagements start from one of these formats. Each is adjusted to the project, the property and the lender involved.</p>
        <Button asChild variant="outline" data-testid="templates-blank" className="rounded-full border-slate-600 bg-transparent"><Link to={user ? "/new" : "/#pricing"}>{user ? "Start blank" : "See fees"}<ArrowRight className="h-4 w-4 ml-2" /></Link></Button>
      </div>
      <div className="lg:col-span-8 grid sm:grid-cols-2 gap-x-10 border-t border-slate-800" data-testid="template-gallery">
        {TEMPLATES.map(({ id, title, tag, form }) => (
          <Link key={id} to={user ? `/new?template=${id}` : "/#pricing"} data-testid={`gallery-${id}`} className="group py-5 border-b border-slate-800 flex gap-4 items-start">
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-100 group-hover:text-emerald-300 transition-colors">{title}</p>
              <p className="text-sm text-slate-500 line-clamp-2 mt-1">{form.description}</p>
            </div>
            <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 pt-1 shrink-0">{tag}</span>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const Process = () => (
  <section className="border-y border-slate-800/60 bg-slate-950/40">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
      <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-14 max-w-xl">How an engagement works</h2>
      <ol className="grid grid-cols-1 md:grid-cols-3 md:divide-x divide-slate-800 border-t border-slate-800">
        {STEPS.map(([t, d], i) => (
          <li key={t} className="pt-8 pb-2 md:px-8 first:md:pl-0">
            <span className="num text-xs text-slate-500">Step {i + 1}</span>
            <h3 className="text-lg font-semibold mt-3 mb-2 text-slate-100">{t}</h3>
            <p className="text-slate-400 leading-relaxed">{d}</p>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

const Scope = ({ user }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
    <div className="grid lg:grid-cols-12 gap-10">
      <h2 className="lg:col-span-4 font-display text-3xl sm:text-4xl font-bold tracking-tight">What every study covers</h2>
      <dl className="lg:col-span-8 grid sm:grid-cols-2 gap-x-12">
        {FEATURES.map(([t, d], i) => (
          <div key={t} className="py-6 border-t border-slate-800">
            <dt className="flex gap-3 items-baseline mb-2"><span className="num text-xs text-slate-600">{String(i + 1).padStart(2, "0")}</span><span className="font-semibold text-slate-100">{t}</span></dt>
            <dd className="text-sm text-slate-400 leading-relaxed pl-8">{d}</dd>
          </div>
        ))}
      </dl>
    </div>
    <div className="mt-16 border border-slate-700 p-8 md:p-10 flex flex-wrap items-center justify-between gap-6">
      <p className="font-serif-display text-2xl md:text-3xl text-slate-100 max-w-2xl">Delivered as a bound PDF and an editable Word file, ready for investors, lenders and planning departments.</p>
      <Button asChild data-testid="cta-start" className="rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400"><Link to={user ? "/new" : "/#pricing"}>{user ? "Start a study" : "See fees"}</Link></Button>
    </div>
  </section>
);

const Faq = () => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 grid lg:grid-cols-12 gap-10">
    <h2 className="lg:col-span-4 font-display text-3xl sm:text-4xl font-bold tracking-tight">Common questions</h2>
    <Accordion type="single" collapsible data-testid="faq" className="lg:col-span-8 border-t border-slate-800">
      {content.faq.map(([q, a], i) => (
        <AccordionItem key={q} value={`f${i}`} className="border-slate-800">
          <AccordionTrigger data-testid={`faq-${i}`} className="text-left text-base">{q}</AccordionTrigger>
          <AccordionContent className="text-slate-400 leading-relaxed">{a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </section>
);

export default function Landing() {
  const { user } = useAuth();
  const { hash, key } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 150);
    return () => clearTimeout(t);
  }, [hash, key]);
  return (
    <div className={user ? "" : "public-site"}>
      <section className={`border-b border-slate-800/60 ${user ? "grid-bg" : ""}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-20 lg:pt-28 lg:pb-24">{user ? <StaffHero /> : <VisitorHero />}</div>
      </section>
      <Templates user={user} />
      <Process />
      <Scope user={user} />
      <Pricing />
      <PricingFaq />
      <SampleComparison />
      <TestimonialWall />
      <Faq />
      {!user && <PublicFooter />}
    </div>
  );
}
